import { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { consumeAdminRedirectPath, consumeAdminSessionTimedOut } from '@/lib/adminSession';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import agniyaLogo from '@/assets/branding/logo.png';

export default function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [needsMfa, setNeedsMfa] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [redirectPath] = useState(() => consumeAdminRedirectPath());
  const [sessionTimedOut] = useState(() => consumeAdminSessionTimedOut());
  const { login, admin } = useAuth();
  const navigate = useNavigate();

  if (admin) {
    return <Navigate to={redirectPath || '/dashboard'} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      toast.error('Please enter username and password');
      return;
    }
    
    setLoading(true);
    try {
      await login(username, password, mfaCode);
      toast.success('Welcome back!');
      navigate(redirectPath || '/dashboard', { replace: true });
    } catch (error) {
      const message = error.response?.data?.detail || 'Invalid credentials';
      if (String(message).toLowerCase().includes('mfa')) {
        setNeedsMfa(true);
      }
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 relative bg-cover bg-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1723155182094-af2f63472d0b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxpbmRpYW4lMjBzcGljZXMlMjBtYXJrZXR8ZW58MHx8fHwxNzY5NzE0MjM1fDA&ixlib=rb-4.1.0&q=85)'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-[#2D2420]/80 to-[#5C4033]/60" />
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <h1 className="text-4xl font-bold mb-4">Agniya Spices</h1>
          <p className="text-lg text-white/80 max-w-md">
            Premium quality spices from the heart of India. Managing your distribution network has never been easier.
          </p>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-[#FAFAF9]">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-10">
            <img
              src={agniyaLogo}
              alt="Agniya Logo"
              className="mx-auto mb-4 w-40 h-auto object-contain"
            />
            <h2 className="text-2xl font-bold text-[#2D2420]">Admin Login</h2>
            <p className="text-[#A8A8A8] mt-2">Pure & Trusted In Every Pinch</p>
          </div>

          {sessionTimedOut ? (
            <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              Your session timed out. Please login again to continue.
            </div>
          ) : null}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="username" className="text-[#5C4033]">Username</Label>
              <Input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter your username"
                className="h-12 bg-white border-[#E5E5E5] focus:border-[#C97A5E] focus:ring-[#C97A5E]/20"
                data-testid="username-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#5C4033]">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="h-12 bg-white border-[#E5E5E5] focus:border-[#C97A5E] focus:ring-[#C97A5E]/20 pr-12"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#A8A8A8] hover:text-[#5C4033]"
                  data-testid="toggle-password"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {needsMfa ? (
              <div className="space-y-2">
                <Label htmlFor="mfa-code" className="text-[#5C4033]">MFA Code</Label>
                <Input
                  id="mfa-code"
                  type="text"
                  inputMode="numeric"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="Enter 6-digit authenticator code"
                  className="h-12 bg-white border-[#E5E5E5] focus:border-[#C97A5E] focus:ring-[#C97A5E]/20"
                />
              </div>
            ) : null}

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#C97A5E] hover:bg-[#B06850] text-white font-medium rounded-xl shadow-md hover:shadow-lg transition-all"
              data-testid="login-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          <p className="text-center text-sm text-[#A8A8A8] mt-8">
            © 2026 Agniya Spices. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
}
